/**
 * Spotlight Effect (Annual Day / achievements)
 * Soft radial glow – celebration without circus. Very low opacity.
 */

import { useEffect, useRef } from 'react'

interface SpotlightProps {
  intensity?: 'low' | 'medium'
}

export function Spotlight({ intensity = 'low' }: SpotlightProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const phaseRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const centerX = canvas.width / 2
    const centerY = canvas.height * 0.4
    const radius = Math.max(canvas.width, canvas.height) * 0.6
    const alpha = intensity === 'low' ? 0.04 : 0.07

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      phaseRef.current += 0.012

      const breathe = 0.92 + Math.sin(phaseRef.current) * 0.08

      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        radius * breathe
      )
      gradient.addColorStop(0, `rgba(255, 215, 0, ${alpha})`)
      gradient.addColorStop(0.5, `rgba(255, 200, 100, ${alpha * 0.3})`)
      gradient.addColorStop(1, 'rgba(255, 215, 0, 0)')

      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
